import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, User, Building, MessageSquare, MessageCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { contactInfo, WHATSAPP_DEFAULT } from '../mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', subject: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken: 'disabled' }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success("Message Sent Successfully!", {
          description: "Thank you for contacting us. We'll respond within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Failed to Send Message", {
        description: "Please try again or email us directly at " + contactInfo.email,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>Contact Us – Request Bulk Quote | Agro Corp India, Daund, Pune</title>
        <meta name="description" content="Contact Agro Corp India for bulk export inquiries. Located in Daund, Pune. Email: info@agrocorpindia.com. Phone: +91 9637471766. Worldwide wholesale agricultural products." />
        <meta name="keywords" content="contact Agro Corp India, agricultural export inquiry, bulk quote request, Pune exporter contact, B2B food supplier India" />
        <link rel="canonical" href="https://www.agrocorpindia.com/contact" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.pexels.com/photos/31949547/pexels-photo-31949547.jpeg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Contact Us</h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Get in touch with us for any inquiries or business partnerships
          </p>
        </div>
      </section>

      {/* Contact Information Cards */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-green-200">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-green-700" />
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Our Location</h3>
                <p className="text-gray-600">{contactInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-amber-200">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-700" />
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Email Us</h3>
                <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-green-700 transition-colors text-sm block mb-1" data-testid="contact-email">
                  {contactInfo.email}
                </a>
                <p className="text-xs text-gray-500 mt-2">For all inquiries</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-green-200">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-green-700" />
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-2">WhatsApp</h3>
                <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-green-700 transition-colors text-sm block mb-1" data-testid="contact-whatsapp">
                  +91 98234 90072
                </a>
                <p className="text-xs text-gray-500 mt-2">Quick response guaranteed</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-green-200">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-green-700" />
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Call Us</h3>
                <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-green-700 transition-colors block mb-1 text-sm">
                  {contactInfo.phone}
                </a>
                <a href={`tel:${contactInfo.phone2}`} className="text-gray-600 hover:text-green-700 transition-colors block text-sm">
                  {contactInfo.phone2}
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="mb-8">
                <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider bg-amber-50 px-4 py-2 rounded-full inline-block mb-4">Get in Touch</span>
                <h2 className="text-4xl font-bold text-green-900 mb-4">Send Us a Message</h2>
                <p className="text-lg text-gray-600">Fill out the form below and our team will get back to you within 24 hours</p>
              </div>
              <Card className="border-2 border-green-200 shadow-xl">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2"><User className="inline w-4 h-4 mr-1" />Full Name *</label>
                      <Input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name" className="w-full border-2 border-gray-200 focus:border-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2"><Mail className="inline w-4 h-4 mr-1" />Email Address *</label>
                      <Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" className="w-full border-2 border-gray-200 focus:border-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2"><Phone className="inline w-4 h-4 mr-1" />Phone Number *</label>
                      <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+1 234 567 8900" className="w-full border-2 border-gray-200 focus:border-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2"><Building className="inline w-4 h-4 mr-1" />Company Name</label>
                      <Input type="text" name="company" value={formData.company} onChange={handleChange} placeholder="Your company name" className="w-full border-2 border-gray-200 focus:border-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2"><MessageSquare className="inline w-4 h-4 mr-1" />Subject *</label>
                      <Input type="text" name="subject" value={formData.subject} onChange={handleChange} required placeholder="What is this regarding?" className="w-full border-2 border-gray-200 focus:border-green-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Message *</label>
                      <Textarea name="message" value={formData.message} onChange={handleChange} required rows={6} placeholder="Tell us more about your inquiry..." className="w-full border-2 border-gray-200 focus:border-green-500" />
                    </div>
                    <Button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn" className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-4 text-lg shadow-lg">
                      {isSubmitting ? 'Sending...' : (<>Send Message <Send className="ml-2 w-5 h-5" /></>)}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-green-900 mb-6">Why Contact Us?</h2>
                <div className="space-y-4">
                  <Card className="border-l-4 border-green-600">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-green-900 mb-2">Product Inquiries</h3>
                      <p className="text-gray-600">Learn more about our products, specifications, and availability</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-amber-600">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-green-900 mb-2">Request Quotation</h3>
                      <p className="text-gray-600">Get competitive pricing for bulk orders and long-term contracts</p>
                    </CardContent>
                  </Card>
                  <Card className="border-l-4 border-green-600">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-green-900 mb-2">Partnership Opportunities</h3>
                      <p className="text-gray-600">Explore distribution and business partnership possibilities</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
              <Card className="overflow-hidden">
                <div className="h-80 bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-16 h-16 text-green-700 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-green-900 mb-2">Visit Our Office</h3>
                    <p className="text-gray-700">{contactInfo.address}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact CTA */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-green-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Prefer to Talk Directly?</h2>
          <p className="text-xl text-green-100 mb-8">Our team is available during business hours to assist you</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WHATSAPP_DEFAULT} target="_blank" rel="noopener noreferrer">
              <Button data-testid="contact-cta-whatsapp" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                <MessageCircle className="mr-2 w-5 h-5" />
                Inquire on WhatsApp
              </Button>
            </a>
            <a href={`tel:${contactInfo.phone}`}>
              <Button className="bg-white hover:bg-gray-100 text-green-800 px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300">
                <Phone className="mr-2 w-5 h-5" />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
